import groupBy from "lodash/groupBy";
import type { ProfileProperties } from "../interfaces/ProfileProperties";

/**
 * Type for cutReference values (can be string, number, or undefined)
 */
type CutReference = string | number | undefined;

/**
 * Result of profile grouping and filtering logic
 */
export interface ProfileDisplayResult<T> {
  /** Whether to show "Siehe Case" instead of profiles */
  showSieheCase: boolean;
  /** Profiles to display (only first group if multiple groups exist) */
  profilesToDisplay: T[];
  /** All groups by cutReference */
  groups: Array<[CutReference, T[]]>;
}

/**
 * Group profiles by cutReference and determine display logic
 *
 * This implements the same logic used in both PDF and DXF generation:
 * - If more than 2 cutReferences exist, show "Siehe Case"
 * - Otherwise, display only the first group of profiles
 *
 * @param profiles - Array of profiles with cutReference property
 * @param getCutReference - Function to extract cutReference from profile
 * @returns ProfileDisplayResult with display logic
 */
export function determineProfileDisplay<T>(
  profiles: T[],
  getCutReference: (profile: T) => CutReference,
): ProfileDisplayResult<T> {
  // Group profiles by cut reference (side)
  const profilesByCutReference = groupBy(profiles, getCutReference);
  const groups = Object.entries(profilesByCutReference) as Array<
    [CutReference, T[]]
  >;

  // If more than 2 cutReferences, show "Siehe Case" - same logic as PDF
  const showSieheCase = groups.length > 2;

  // Draw only the first group if not showing "Siehe Case"
  const firstGroup = groups[0];
  const profilesToDisplay = showSieheCase || !firstGroup ? [] : firstGroup[1];

  return {
    showSieheCase,
    profilesToDisplay,
    groups,
  };
}

/**
 * Helper for ProfileProperties
 */
export function determineProfilePropertiesDisplay(
  profiles: ProfileProperties[],
): ProfileDisplayResult<ProfileProperties> {
  return determineProfileDisplay(profiles, (p) => p.cutReference);
}
